// =============================================================================
// discoveryPrompt.ts — build the AI scholarship-discovery prompt (v0.2.50)
// =============================================================================
//
// The Inbox's "Generate discovery prompt" panel renders a prompt the family
// copies into their own AI (ChatGPT / Claude / Gemini — any). The AI answers
// with JSON in the AiDiscoveryImportPayload shape, which the "Import AI
// Discoveries" form ingests (see aiDiscoveryImport.ts). No AI runs inside
// the app. This module is the prompt's single source of truth — the output
// contract it prints IS the schema the importer accepts.
//
// The template was synthesized from a three-lens design panel (counselor /
// importer-strictness / privacy) on 2026-09-20, then hardened by a
// three-lens adversarial review the same day.
//
// PRIVACY — the prompt is built from an ALLOW-LIST of PROFILE fields. It
// never reads the legal middle/last name (first name only), date of birth
// (grade + graduation instead), SSN, street address, ZIP, region, phone,
// email, church name, homeschool legal / admin names, parent labels, class
// rank, honors/activity descriptions, course grades, test dates or section
// scores, or target-school links/notes. Income bracket / EFC are included
// only when `includeFinancial` is on; the free-text special_circumstances
// field only when `includeSpecialCircumstances` is on.
//
// What the prompt DOES carry verbatim, because it is the matching signal:
// organization names the family typed for activities, work, and service
// (an organization that matches the profile's church_name is replaced with
// "their church"), and the names + sponsors of every already-known award
// (tracked, dismissed, pending) so the AI does not repeat them. The panel's
// privacy note says so. The test script (scripts/test-discovery-prompt.ts)
// asserts the exclusions against a fully-populated fictional profile.
//
// Every profile field may be null/empty (profiles fill in over time). The
// core bullets (name, location, grade, schooling, GPA, tests, majors) always
// render — with "not provided" — so the AI knows a fact is absent rather
// than silently missing; list bullets are omitted when empty. Date and
// timeline arithmetic is done HERE (cycle summary, horizon date, national
// cap), never delegated to the AI. Enum-like stored values (e.g. `hispanic`,
// `60k_to_100k`, `some_college`) are rendered through humanize().
// =============================================================================

import type { Profile, TargetSchool } from '@scholarship-app/types';
import { US_STATES } from './usStates';
import { humanize } from './humanize';

export interface NamedAward {
  name: string;
  sponsor: string;
}

export interface DiscoveryPromptOptions {
  /** How many scholarships to ask for. */
  itemCount: number;
  /** Include income bracket / income estimate / EFC in the prompt. */
  includeFinancial: boolean;
  /** Include the free-text special_circumstances field (may be sensitive). */
  includeSpecialCircumstances: boolean;
  /** Free-text steering from the family ("prioritize art scholarships"). */
  focusNotes: string;
}

export interface DiscoveryPromptInput {
  /** Applicant doc display_name — used only as a first-name fallback. */
  applicantDisplayName: string;
  profile: Profile | null;
  targetSchools: TargetSchool[];
  /** Scholarships already tracked for this applicant (any status, incl. archived). */
  tracked: NamedAward[];
  /** Household's dismissed inbox items. */
  dismissed: NamedAward[];
  /** Inbox items still pending review (new / reviewing / snoozed). */
  pending: NamedAward[];
  options: DiscoveryPromptOptions;
  today: Date;
}

export const DISCOVERY_PROMPT_ITEM_COUNT_MIN = 5;
export const DISCOVERY_PROMPT_ITEM_COUNT_MAX = 40;

export const DEFAULT_DISCOVERY_PROMPT_OPTIONS: DiscoveryPromptOptions = {
  itemCount: 20,
  includeFinancial: true,
  includeSpecialCircumstances: true,
  focusNotes: '',
};

// ---------- small formatting helpers ----------

function clean(v: unknown): string {
  return typeof v === 'string' ? v.trim() : '';
}

function has(v: unknown): boolean {
  if (v === null || v === undefined) return false;
  if (typeof v === 'string') return v.trim().length > 0;
  if (Array.isArray(v)) return v.length > 0;
  return true;
}

/**
 * Display form of a stored value. Enum-like values (`some_college`,
 * `hispanic`, `60k_to_100k`) go through humanize(); anything with capitals,
 * spaces, or punctuation is free text and is left exactly as typed (so
 * "LCMS" does not become "Lcms").
 */
function label(v: unknown): string {
  const s = clean(v);
  if (!s) return '';
  return /^[a-z0-9_-]+$/.test(s) ? humanize(s) : s;
}

/** Calendar date in the DEVICE's local time zone (what the family means by "today"). */
function localIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** ISO date of a calendar date constructed with Date.UTC(...). */
function utcIso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function money(n: number | null | undefined): string | null {
  if (typeof n !== 'number' || !Number.isFinite(n)) return null;
  return '$' + Math.round(n).toLocaleString('en-US');
}

function joinList(items: string[], sep = '; '): string {
  return items.map((s) => s.trim()).filter((s) => s.length > 0).join(sep);
}

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const GRADE_WORDS: Record<number, string> = { 9: 'freshman', 10: 'sophomore', 11: 'junior', 12: 'senior' };

/** "NC" → "North Carolina"; anything unrecognized is passed through. */
function stateName(code: string): string {
  const c = clean(code).toUpperCase();
  return US_STATES.find((s) => s.code === c)?.name ?? clean(code);
}

/**
 * Read a month/year out of the expected-graduation value. The Profile page
 * stores it as free text (placeholder "YYYY-MM"), so accept "2027-05",
 * "2027-05-31", "05/2027", "5/2027", "May 2027", "May, 2027", or "2027".
 * The label is ALWAYS rebuilt from the parsed parts — raw text is never
 * echoed, so a note a parent typed into the field cannot reach the prompt.
 */
function graduationParts(raw: string): { label: string; year: number | null; month: number | null } {
  const s = clean(raw);
  if (!s) return { label: '', year: null, month: null };
  let year: number | null = null;
  let month: number | null = null;

  const iso = s.match(/^(\d{4})-(\d{1,2})(?:-\d{1,2})?/);
  const slash = s.match(/^(\d{1,2})\s*\/\s*(\d{4})/);
  if (iso) {
    year = Number(iso[1]);
    const m = Number(iso[2]);
    if (m >= 1 && m <= 12) month = m;
  } else if (slash) {
    year = Number(slash[2]);
    const m = Number(slash[1]);
    if (m >= 1 && m <= 12) month = m;
  } else {
    const anyYear = s.match(/\b(20\d{2})\b/);
    if (anyYear) year = Number(anyYear[1]);
    const lower = s.toLowerCase();
    const idx = MONTHS.findIndex((m) => new RegExp(`\\b${m.toLowerCase().slice(0, 3)}`).test(lower));
    if (idx >= 0) month = idx + 1;
  }

  if (year === null) return { label: '', year: null, month: null };
  return { label: month ? `${MONTHS[month - 1]} ${year}` : String(year), year, month };
}

const SCHOOLING_LABELS: Record<Profile['schooling_type'], string> = {
  homeschool_npns: 'Homeschool (registered non-public home school)',
  public: 'Public school',
  private: 'Private school',
  charter: 'Charter school',
  online: 'Online school',
};

const CC_ROLE_LABELS: Record<Profile['classical_conversations_role'], string | null> = {
  none: null,
  student_only: 'Classical Conversations: student',
  student_with_parent_tutor: 'Classical Conversations: student, with a parent who tutors',
  student_with_parent_director: 'Classical Conversations: student, with a parent who directs a community',
};

const SCHOOL_TYPE_LABELS: Record<TargetSchool['school_type'], string> = {
  private: 'private',
  public_in_state: 'public, in-state',
  public_out_of_state: 'public, out-of-state',
};

/**
 * Citizenship bucketed into neutral phrases — enough for eligibility rules
 * (most awards require citizen or permanent-resident status) without passing
 * the raw text through. The app stores the enum values `us_citizen`,
 * `us_permanent_resident`, `daca`, `international` (see humanize.ts); free
 * text is matched best-effort. Anything unrecognized returns null and the
 * line is OMITTED — the prompt never asserts a status it cannot read.
 */
export function citizenshipBucket(raw: string): string | null {
  // Underscores/dashes → spaces so the stored enums read as words; drop
  // periods so "U.S." and "US" compare the same.
  const s = clean(raw).toLowerCase().replace(/[_-]+/g, ' ').replace(/\./g, '').replace(/\s+/g, ' ').trim();
  if (!s) return null;
  const NOT = 'not a U.S. citizen or permanent resident';
  if (/\bdaca\b/.test(s)) return 'DACA recipient';
  if (/permanent|green\s*card|lawful/.test(s)) return 'U.S. permanent resident';
  if (/(non|not)\s*(a\s+)?(us\s+)?citizen|\binternational\b|\bvisa\b|\b[fj] ?1\b/.test(s)) return NOT;
  if (/\b(us|usa|united states|american)\b/.test(s)) return 'U.S. citizen';
  if (/^(citizen|native born|natural(ized)?( citizen)?|dual citizen)$/.test(s)) return 'U.S. citizen';
  return null;
}

/**
 * First name only — the one identifier the prompt carries. Prefers the
 * profile's preferred name, then legal first name, then the applicant's
 * display name (which may be a full name, or "Last, First"). Always a single
 * token with punctuation stripped; never a middle or last name.
 */
export function applicantFirstName(profile: Profile | null, displayName: string): string {
  const candidates = [profile?.preferred_name, profile?.legal_first_name, displayName];
  for (const c of candidates) {
    let s = clean(c);
    if (!s) continue;
    // "Smith, Jordan" → "Jordan"
    if (s.includes(',')) s = s.slice(s.indexOf(',') + 1).trim();
    const first = s.split(/\s+/)[0].replace(/[,.;:()"']+$/g, '');
    if (first) return first;
  }
  return 'the student';
}

/**
 * Replace an organization name that is the family's church with a neutral
 * phrase. The profile's church_name is off the allow-list, but families
 * routinely enter the congregation as the organization of an activity or
 * service entry, which would otherwise carry it straight into the prompt.
 */
function redactChurch(org: string, churchName: string): string {
  const o = clean(org);
  const c = clean(churchName).toLowerCase();
  if (!o || c.length < 3) return o;
  return o.toLowerCase().includes(c) ? 'their church' : o;
}

// ---------- timeline (computed here, never by the AI) ----------

interface Cycle {
  summary: string;
  /** Last day of the month four months after graduation (or today + 24 months). */
  horizonIso: string;
  horizonLabel: string;
  gradeWord: string | null;
}

function lastDayOfMonth(year: number, month1to12: number): Date {
  // Day 0 of the next month = last day of this month (UTC calendar date).
  return new Date(Date.UTC(year, month1to12, 0));
}

function computeCycle(profile: Profile | null, today: Date, firstName: string): Cycle {
  // Local calendar, not UTC: after 8 PM Eastern the UTC date is already
  // tomorrow, and the family's "today" is the local one.
  const nowYear = today.getFullYear();
  const nowMonth0 = today.getMonth();
  const todayIso = localIso(today);
  // The school year that is in progress ends in the spring of `schoolYearEnd`.
  const schoolYearEnd = nowMonth0 >= 6 ? nowYear + 1 : nowYear;
  const rawGrade = profile?.current_grade;
  let grade: number | null = typeof rawGrade === 'number' && rawGrade >= 1 && rawGrade <= 12 ? rawGrade : null;
  const grad = graduationParts(profile?.expected_graduation ?? '');
  let gradYear = grad.year;
  let gradMonth = grad.month;
  let assumedGraduation = false;

  if (gradYear === null && grade !== null) {
    gradYear = schoolYearEnd + (12 - grade);
    gradMonth = 5;
    assumedGraduation = true;
  }
  if (grade === null && gradYear !== null) {
    const derived = 12 - (gradYear - schoolYearEnd);
    if (derived >= 9 && derived <= 12) grade = derived;
  }

  const monthLabel = (d: Date) => `${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;

  if (gradYear === null) {
    const h = lastDayOfMonth(nowYear + 2, nowMonth0 + 1);
    return {
      summary: 'Grade and graduation date were not provided — assume a high-school senior applying this cycle, and say so in notes.',
      horizonIso: utcIso(h),
      horizonLabel: monthLabel(h),
      gradeWord: null,
    };
  }

  const gm = gradMonth ?? 5;
  const horizonMonth0 = gm - 1 + 4; // four months after graduation
  const h = lastDayOfMonth(gradYear + Math.floor(horizonMonth0 / 12), (horizonMonth0 % 12) + 1);
  const gradLabel = `${MONTHS[gm - 1]} ${gradYear}${assumedGraduation ? ' (assumed from grade)' : ''}`;

  // A stale profile whose graduation (and horizon) is already behind us:
  // never print an inverted window — treat the student as a college student.
  if (utcIso(h) < todayIso) {
    const h2 = lastDayOfMonth(nowYear + 1, nowMonth0 + 1);
    return {
      summary: `${firstName}'s expected high-school graduation (${gradLabel}) is in the past, so treat ${firstName} as an entering or current college student: look for awards open to enrolled undergraduates and transfer students, with deadlines in the next 12 months.`,
      horizonIso: utcIso(h2),
      horizonLabel: monthLabel(h2),
      gradeWord: null,
    };
  }

  const horizonLabel = monthLabel(h);
  const gradeWord = grade !== null ? GRADE_WORDS[grade] ?? null : null;

  let summary: string;
  if (grade === 12) {
    summary = `${firstName} is a high-school senior (12th grade), expected to graduate in ${gradLabel} and enroll in college in fall ${gradYear}. This is the main scholarship season; the window closes around ${horizonLabel}.`;
  } else if (grade === 11) {
    summary = `${firstName} is a junior (11th grade) now, expected to graduate in ${gradLabel} and enroll in college in fall ${gradYear}. The rest of this school year is for junior-eligible awards and early senior-cycle awards; senior year (fall ${gradYear - 1} – spring ${gradYear}) is the main scholarship season; the window closes around ${horizonLabel}.`;
  } else if (grade !== null && grade <= 10) {
    const word = GRADE_WORDS[grade] ? `a ${GRADE_WORDS[grade]} (${ordinal(grade)} grade)` : `in ${ordinal(grade)} grade`;
    summary = `${firstName} is ${word}, expected to graduate in ${gradLabel}. Include awards open to younger students now, and note that the main cycle will be senior year (fall ${gradYear - 1} – spring ${gradYear}) — for those, say in notes which grade they apply in.`;
  } else {
    summary = `${firstName} is expected to graduate high school in ${gradLabel} and enroll in college in fall ${gradYear}; the window closes around ${horizonLabel}.`;
  }

  return { summary, horizonIso: utcIso(h), horizonLabel, gradeWord };
}

// ---------- section builders ----------

function snapshotLines(profile: Profile | null, firstName: string, includeSpecial: boolean): string[] {
  const out: string[] = [];
  out.push(`- Name: ${firstName} (first name only; use it as applicant_name)`);

  // Location — city + spelled-out state; never street, ZIP, or region.
  const city = clean(profile?.address?.city);
  const state = clean(profile?.address?.state);
  if (city && state) out.push(`- Location: ${city}, ${stateName(state)} (infer the county and metro area yourself)`);
  else if (state) out.push(`- Location: state only — ${stateName(state)} (infer the region yourself)`);
  else if (city) out.push(`- Location: ${city} (state not provided — infer it if you can)`);
  else out.push('- Location: not provided — skip local and state awards and say so in notes');

  // Grade + graduation (label rebuilt from parsed parts — never raw text).
  const grade = profile?.current_grade;
  const grad = graduationParts(profile?.expected_graduation ?? '');
  const gradeBits: string[] = [];
  if (typeof grade === 'number') gradeBits.push(`${ordinal(grade)} grade${GRADE_WORDS[grade] ? ` (${GRADE_WORDS[grade]})` : ''}`);
  if (grad.label) gradeBits.push(`expected high-school graduation: ${grad.label}`);
  out.push(`- Grade: ${gradeBits.length ? gradeBits.join('; ') : 'not provided'}`);

  // Schooling.
  if (profile?.schooling_type) {
    const bits = [SCHOOLING_LABELS[profile.schooling_type] ?? label(profile.schooling_type)];
    if (profile.schooling_type === 'homeschool_npns') {
      if (has(profile.homeschool_co_op)) bits.push(`co-op: ${clean(profile.homeschool_co_op)}`);
      const cc = profile.classical_conversations_role ? CC_ROLE_LABELS[profile.classical_conversations_role] : null;
      if (cc) bits.push(cc);
      if (profile.hslda_member) bits.push('HSLDA member: yes');
    }
    out.push(`- Schooling: ${bits.join('; ')}`);
  } else {
    out.push('- Schooling: not provided');
  }

  // GPA (class rank is never rendered).
  const gpa: string[] = [];
  if (typeof profile?.gpa_weighted === 'number') gpa.push(`${profile.gpa_weighted} weighted`);
  if (typeof profile?.gpa_unweighted === 'number') gpa.push(`${profile.gpa_unweighted} unweighted`);
  out.push(`- GPA: ${gpa.length ? gpa.join(' / ') : 'not provided'}`);

  // Tests — best composites only; no dates, no section scores.
  const ts = profile?.test_scores;
  const best = (arr: Array<{ score: number }> | undefined) =>
    (arr ?? []).reduce<number | null>((m, s) => (typeof s?.score === 'number' && (m === null || s.score > m) ? s.score : m), null);
  const tests: string[] = [];
  if (ts) {
    const act = best(ts.act);
    const sat = best(ts.sat);
    const psat = best(ts.psat);
    if (act !== null) tests.push(`ACT ${act} composite${ts.act_retake_planned ? ' (retake planned)' : ''}`);
    else if (ts.act_retake_planned) tests.push('ACT planned');
    if (sat !== null) tests.push(`SAT ${sat} total`);
    else if (ts.sat_planned) tests.push('SAT not taken yet (planned)');
    if (psat !== null) tests.push(`PSAT ${psat}`);
  }
  out.push(`- Test scores: ${tests.length ? tests.join('; ') : 'none yet'}`);

  // Dual enrollment — institution + counts + course names; no grades/statuses.
  const de = profile?.dual_enrollment;
  if (de && (has(de.institution_name) || has(de.courses))) {
    const courses = (de.courses ?? []).filter((c) => c.status !== 'dropped');
    const names = courses.map((c) => clean(c.course_name)).filter(Boolean);
    const where = has(de.institution_name) ? clean(de.institution_name) : 'college courses';
    const count = courses.length ? `${courses.length} course${courses.length === 1 ? '' : 's'} completed or in progress` : '';
    out.push(`- Dual enrollment: ${where}${count ? ` — ${count}` : ''}${names.length ? ` (${names.join(', ')})` : ''}`);
  }

  const majors = (profile?.intended_majors ?? []).map(clean).filter(Boolean);
  out.push(`- Intended majors: ${majors.length ? majors.join('; ') : 'undecided'}`);
  // Majors, career path, and strengths are free text — rendered as typed.
  if (has(profile?.career_path)) out.push(`- Career path: ${clean(profile?.career_path)}`);
  if (has(profile?.academic_strengths)) out.push(`- Academic strengths: ${joinList(profile!.academic_strengths.map(clean))}`);

  const church = clean(profile?.church_name);
  if (has(profile?.extracurriculars)) {
    out.push(`- Extracurriculars: ${joinList(profile!.extracurriculars.map((e) => {
      const detail = joinList([clean(e.role), redactChurch(e.organization, church), typeof e.hours_per_week === 'number' && e.hours_per_week > 0 ? `~${e.hours_per_week} hrs/week` : ''], ', ');
      return detail ? `${clean(e.name)} — ${detail}` : clean(e.name);
    }))}`);
  }
  if (has(profile?.honors_awards)) {
    out.push(`- Honors & awards: ${joinList(profile!.honors_awards.map((h) => (has(h.level) ? `${clean(h.name)} (${label(h.level)})` : clean(h.name))))}`);
  }
  if (has(profile?.work_experience)) {
    out.push(`- Work experience: ${joinList(profile!.work_experience.map((w) => {
      const role = clean(w.role) || clean(w.name);
      const org = redactChurch(w.organization, church);
      return org ? `${role} at ${org}` : role;
    }))}`);
  }
  const serviceOrgs = Array.from(new Set((profile?.community_service_entries ?? []).map((c) => redactChurch(c.organization, church)).filter(Boolean)));
  const serviceHours = profile?.community_service_hours_total;
  if (typeof serviceHours === 'number' && serviceHours > 0) {
    out.push(`- Community service: ${serviceHours} hours total${serviceOrgs.length ? ` — ${serviceOrgs.join('; ')}` : ''}`);
  } else if (serviceOrgs.length) {
    out.push(`- Community service: ${serviceOrgs.join('; ')}`);
  }
  const languages = (profile?.languages_spoken ?? []).map(label).filter(Boolean);
  if (languages.length && !(languages.length === 1 && /^english$/i.test(languages[0]))) {
    out.push(`- Languages: ${languages.join('; ')}`);
  }

  // Faith — affiliation + denomination/synod + church CITY; never the church name.
  if (has(profile?.religious_affiliation) || has(profile?.lutheran_synod)) {
    const bits: string[] = [];
    const aff = label(profile?.religious_affiliation);
    const synod = label(profile?.lutheran_synod);
    bits.push(aff && synod ? `${aff} — ${synod}` : aff || synod);
    if (has(profile?.church_city)) bits.push(`congregation in ${clean(profile?.church_city)}`);
    out.push(`- Religious affiliation: ${bits.join('; ')}`);
  }

  if (profile) {
    const degrees = joinList((profile.parent_education_levels ?? []).map((p) => label(p.highest_degree)));
    out.push(`- First-generation college student: ${profile.first_generation_college_student ? 'yes' : 'no'}${degrees ? `; parents' highest education: ${degrees}` : ''}`);
  }

  const background: string[] = [];
  if (has(profile?.ethnicity)) background.push(`Ethnicity: ${joinList(profile!.ethnicity.map(label))}`);
  if (has(profile?.gender)) background.push(`Gender: ${label(profile?.gender)}`);
  const cit = citizenshipBucket(profile?.citizenship ?? '');
  if (cit) background.push(`Citizenship: ${cit}`);
  if (background.length) out.push(`- ${background.join('; ')}`);

  if (has(profile?.tags)) out.push(`- Profile tags: ${joinList(profile!.tags)}`);

  if (includeSpecial && has(profile?.special_circumstances)) {
    out.push(`- Circumstances the family chose to share (use for matching only, never repeat it in the output): ${clean(profile?.special_circumstances)}`);
  }

  return out;
}

const FINANCIAL_NOT_SHARED =
  'Household finances are intentionally not shared. Still include need-based awards the student otherwise fits: set "need_based": true and say in "notes" that the family must check the income limit themselves.';

function financialSection(profile: Profile | null, include: boolean): string {
  const lines: string[] = [];
  if (include) {
    // A bracket is the family-facing figure; the exact estimate is rendered
    // only when there is no bracket to stand in for it.
    if (has(profile?.household_income_bracket)) lines.push(`- Household income bracket: ${label(profile?.household_income_bracket)}`);
    else {
      const income = money(profile?.household_income_estimate);
      if (income) lines.push(`- Household income estimate: ${income}`);
    }
    const efc = money(profile?.efc_estimate);
    if (efc) lines.push(`- Estimated EFC / SAI: ${efc}`);
  }
  if (!include || lines.length === 0) {
    return ['## Financial information', FINANCIAL_NOT_SHARED].join('\n');
  }
  return [
    '## Financial information (shared by the family)',
    ...lines,
    'Use this to set need_based honestly and to skip need-based awards whose published income limits the family clearly exceeds. Where limits are unpublished, include the award and flag it in notes.',
  ].join('\n');
}

function admissionDeadlines(s: TargetSchool): string {
  const d = s.admission_deadlines;
  if (!d) return '';
  const bits: string[] = [];
  if (has(d.early_decision)) bits.push(`ED ${clean(d.early_decision)}`);
  if (has(d.early_action)) bits.push(`EA ${clean(d.early_action)}`);
  if (has(d.regular_decision)) bits.push(`RD ${clean(d.regular_decision)}`);
  if (d.rolling) bits.push('rolling admission');
  return bits.join(' / ');
}

function targetSchoolsSection(schools: TargetSchool[], majors: string[], state: string): string {
  const majorText = majors.length
    ? majors.length === 1 ? majors[0] : `${majors.slice(0, -1).join(', ')} and ${majors[majors.length - 1]}`
    : "the student's intended field (undeclared — treat as general)";
  const lines: string[] = ['## Target schools'];
  if (schools.length === 0) {
    lines.push(`(no target schools entered yet — suggest institutional awards at public universities in ${state ? stateName(state) : "the student's state"} that fit ${majorText}, and say so in notes)`);
  } else {
    schools.forEach((s, i) => {
      const bits = [
        s.tier === 'primary' ? 'primary' : 'backup',
        SCHOOL_TYPE_LABELS[s.school_type] ?? label(s.school_type),
        joinList([clean(s.city), clean(s.state)], ', '),
        admissionDeadlines(s),
      ].filter(Boolean);
      lines.push(`${i + 1}. ${clean(s.school_name)} — ${bits.join('; ')}`);
    });
  }
  lines.push(
    `For each school, look for: (a) institutional merit scholarships, including automatic tiers — state the published GPA/test thresholds so the family can see whether a higher test score would unlock a bigger award; (b) honors-college scholarships; (c) departmental scholarships for ${majorText}. Note when an award requires admission first or a separate application with its own deadline, and any priority deadline that gates merit consideration. Skip any already listed under "Do not return these".`
  );
  return lines.join('\n');
}

function exclusionsSection(tracked: NamedAward[], dismissed: NamedAward[], pending: NamedAward[]): string {
  const render = (arr: NamedAward[]) => {
    const lines = arr
      .map((a) => joinList([clean(a.name), clean(a.sponsor)], ' — '))
      .filter(Boolean);
    return lines.length ? lines.join('\n') : '(none)';
  };
  return [
    '## Do not return these',
    'Already tracked:',
    render(tracked),
    'Previously dismissed:',
    render(dismissed),
    'Pending review in the inbox:',
    render(pending),
    'My importer dedupes on name + sponsor only, so do not re-list any of the above under a different name, an abbreviation, a year, or as one of its sub-programs. Use each award\'s official name (no trailing year) and the sponsor\'s official organization name, and list one program once — put tiers in amount_notes.',
  ].join('\n');
}

function whatToFindSection(count: number, city: string, state: string, focusNotes: string): string {
  const nationalCap = Math.ceil(count / 4);
  const place = joinList([city || "the student's city", state ? stateName(state) : "the student's state"], ', ');
  const stateText = state ? stateName(state) : "the student's state";
  const lines = [
    '## What to find',
    `Return ${count} awards spread across these buckets. Fill every bucket where the student qualifies:`,
    `1. Local/regional — ${place}, and the surrounding county and metro area (infer the county yourself): the community foundation (name it), Rotary/Kiwanis/Lions/Elks/Optimist clubs, credit unions, utilities and co-ops, county professional associations, chambers of commerce, large regional employers. This bucket is the most under-searched and least competitive; aim for at least a quarter of the list here.`,
    `2. State — ${stateText} agencies and grant programs, statewide foundations, state professional associations, and state-specific programs at the target schools.`,
    `3. Affinity — every identity and affiliation in the snapshot: ethnicity, gender, faith/denomination, schooling type, first-generation status, intended major and career path, specific activities and honors. Narrow eligibility the student meets exactly beats broad pools.`,
    `4. Employer/membership — awards for children of employees or members (credit unions, unions, insurers, fraternal and professional associations, congregations/denominational bodies), especially organizations named in the snapshot or the family's notes; in notes, tell the family what to ask their employer, church, or credit union.`,
    `5. National — at most ${nationalCap}. Prefer awards whose criteria this student meets exactly over famous programs with tens of thousands of applicants.`,
    `6. Institutional — the target-school awards described above.`,
    `Prioritize fit and realistic odds over dollar size. Include several small, low-competition local awards; a $500 award with 30 applicants beats a $20,000 award with 50,000. Return an award only if the student meets every stated eligibility requirement. If one requirement is uncertain, say which one in notes and lower match_strength.`,
  ];
  if (focusNotes) {
    lines.push('', '### Notes from the family', focusNotes);
  }
  return lines.join('\n');
}

function timingSection(cycle: Cycle, today: Date): string {
  const nowBit = cycle.gradeWord
    ? `plus awards that current ${cycle.gradeWord}s can apply for now (some are ${cycle.gradeWord}-only)`
    : 'plus awards the student can apply for now at their current level';
  return [
    '## Timing',
    `Today is ${localIso(today)}. ${cycle.summary}`,
    `Include awards whose next deadline falls between today and ${cycle.horizonIso}, ${nowBit}. For recurring annual awards whose current cycle has already closed, give the expected next-cycle deadline and write "deadline estimated from the prior cycle" in notes.`,
    `Format deadline_approx as "YYYY-MM-DD". If only the month is known, use the FIRST day of that month and begin notes with "Deadline estimated — confirm the exact day." If the award is genuinely rolling, set deadline_rolling true and deadline_approx null. If the deadline is unknown, set deadline_approx null and deadline_rolling false and explain in notes.`,
  ].join('\n');
}

function qualityRulesSection(count: number): string {
  return [
    '## Quality rules',
    '- Real awards only. If you are not confident an award still exists, leave it out. Prefer the official sponsor or school page over aggregator listings (Scholarships.com, Fastweb, Bold, etc.).',
    '- url_verified is true ONLY if you actually opened and confirmed the URL during this session. Otherwise false — never mark true from memory. If you could not verify an award but are confident it is real, keep it with url_verified false and begin notes with "UNVERIFIED: " followed by what to double-check.',
    '- amount_min and amount_max are plain numbers (no "$" or commas). A single fixed amount: set both equal. Unknown: null, and explain in amount_notes.',
    '- match_strength: high = meets every criterion and the award targets this profile; medium = eligible but a general pool; low = eligible with a weak or unverified fit.',
    '- competitiveness: low (local or small applicant pool), medium, high (national with thousands of applicants), null if unknown.',
    '- effort: S = a form or under 30 minutes; M = one short essay or one recommendation; L = multiple essays, portfolio, video, or interview.',
    '- homeschool_friendly: true if the sponsor accepts parent-issued transcripts and non-counselor recommendations; false when an award requires a school-counselor nomination, class rank, or an accredited-school transcript with no homeschool path; null if unknown.',
    '- need_based: true if income or financial need is part of the selection criteria.',
    `- why_match: one or two sentences naming the specific facts from the snapshot that satisfy the award's criteria, written for a parent scanning ${count} items in a minute.`,
    '- requirements_summary: the actual application components (essay count and length, recommendations, transcript, test scores, portfolio, interview, FAFSA).',
    '- tags: lowercase snake_case, choose from: local, state, national, need_based, merit_based, homeschool, first_gen, faith_based, female, male, hispanic, black, asian, native_american, community_service, leadership, stem, arts, essay_contest, no_essay, renewable, employer, plus <field>_major (for example nursing_major). Add a heritage tag only when the award is restricted by heritage.',
    "- notes: anything the family must double-check — eligibility gray areas, estimated deadlines, aggregator sourcing, whether financial documents are required. Never restate the applicant's personal details here.",
  ].join('\n');
}

function outputContractSection(firstName: string, count: number, today: Date): string {
  return [
    '## Output format',
    `Return exactly this structure and nothing else: {"ai_discovery_run":{"run_date":"${localIso(today)}","applicant_name":"${firstName}","items":[ …items… ]}}`,
    '',
    'Each item has exactly these 18 keys:',
    '- name: string. The award\'s official name, no trailing year.',
    '- sponsor: string. The sponsoring organization\'s official name.',
    '- url: string or null. The official page; null (never "") when unknown.',
    '- url_verified: boolean.',
    '- amount_min: number or null.',
    '- amount_max: number or null. Equal to amount_min for a fixed amount; the upper bound for a range.',
    '- amount_notes: string. How the amount works ("renewable 4 years", "up to full tuition"); "" when nothing to add, never null.',
    '- deadline_approx: string "YYYY-MM-DD" or null (see Timing).',
    '- deadline_rolling: boolean.',
    '- match_strength: "high" | "medium" | "low".',
    '- why_match: string.',
    '- competitiveness: "low" | "medium" | "high" | null ("extremely_high" is not allowed).',
    '- effort: "S" | "M" | "L".',
    '- tags: array of lowercase snake_case strings from the vocabulary above.',
    '- requirements_summary: string.',
    '- need_based: boolean.',
    '- homeschool_friendly: boolean or null.',
    '- notes: string or null.',
    '',
    'One complete example item (illustrative only — do not return this item):',
    '{"name":"Riverside County Community Foundation Future Nurses Award","sponsor":"Riverside County Community Foundation","url":"https://example.org/scholarships/future-nurses","url_verified":false,"amount_min":1000,"amount_max":1000,"amount_notes":"One-time award; four recipients per year.","deadline_approx":"2027-03-01","deadline_rolling":false,"match_strength":"high","why_match":"County resident with an intended nursing major; 140 hospital and food-pantry volunteer hours satisfy the 50-hour service requirement.","competitiveness":"low","effort":"M","tags":["local","nursing_major","community_service","merit_based"],"requirements_summary":"Online form, 500-word essay, one recommendation, transcript (parent-issued accepted).","need_based":false,"homeschool_friendly":true,"notes":"Deadline estimated from the prior cycle — confirm the exact day when the page updates in January."}',
    '',
    `Return exactly ${count} items, ordered by match_strength then by nearest deadline. If they will not fit in one reply, return fewer COMPLETE items — never a cut-off array. Never pad with invented awards to reach the count.`,
    'Begin. Return the JSON only.',
  ].join('\n');
}

// ---------- the prompt ----------

export function buildDiscoveryPrompt(input: DiscoveryPromptInput): string {
  const { profile, targetSchools, tracked, dismissed, pending, today } = input;
  const count = Math.min(
    DISCOVERY_PROMPT_ITEM_COUNT_MAX,
    Math.max(DISCOVERY_PROMPT_ITEM_COUNT_MIN, Math.round(input.options.itemCount) || DEFAULT_DISCOVERY_PROMPT_OPTIONS.itemCount)
  );
  const firstName = applicantFirstName(profile, input.applicantDisplayName);
  const majors = (profile?.intended_majors ?? []).map(clean).filter(Boolean);
  const state = clean(profile?.address?.state);
  const city = clean(profile?.address?.city);
  const focus = clean(input.options.focusNotes);
  const cycle = computeCycle(profile, today, firstName);

  const sections: string[] = [];

  sections.push([
    `# Scholarship discovery request for ${firstName}`,
    '',
    '## Your role',
    `You are an experienced independent scholarship counselor. A parent or guardian is using you to find college scholarships for their high-school student, ${firstName}, and will review everything you return before acting on it. Find real, currently offered scholarships that ${firstName} has a realistic chance of winning, and return them as structured JSON for import into a tracking app. Today is ${localIso(today)}. If you can browse the web, verify each award on its official page. If you cannot browse, work from what you know, set url_verified to false, and say in notes whenever you could not confirm a detail. Never invent an award, sponsor, amount, or deadline. Use only the facts below; do not ask follow-up questions — where something is not provided, work with what is here. Your entire reply must be ONE JSON object in the format under "Output format" — no greeting, no explanation, no markdown fences.`,
  ].join('\n'));

  sections.push(['## Applicant snapshot', ...snapshotLines(profile, firstName, input.options.includeSpecialCircumstances)].join('\n'));
  sections.push(financialSection(profile, input.options.includeFinancial));
  sections.push(targetSchoolsSection(targetSchools, majors, state));
  sections.push(exclusionsSection(tracked, dismissed, pending));
  sections.push(whatToFindSection(count, city, state, focus));
  sections.push(timingSection(cycle, today));
  sections.push(qualityRulesSection(count));
  sections.push(outputContractSection(firstName, count, today));

  return sections.join('\n\n');
}
